import { Injectable } from '@angular/core';
import { ConcretePlan } from 'src/app/pages/new-billing/models/plan.model';
import { UnitPriceOverride } from '../../pages/new-billing/types/api.types';
import { convertCentsToDollars, normalizePrice } from '../../util/price-formatter.util';
import { GeoLocationService } from '../geo-location';
import { GrowthBookService } from '../growthbook/service';

export interface CurrencyInfo {
  symbol: string;
  code: string;
  locale: string;
  position: 'before' | 'after';
}

export interface PriceParts {
  symbol: string;
  whole: string;
  decimal: string;
  position: 'before' | 'after';
}

// Supported currency codes for type safety
export type SupportedCurrencyCode = 'USD' | 'PHP' | 'INR' | 'EUR' | 'GBP' | 'CAD' | 'AUD' | 'JPY' | 'CNY' | 'BRL' | 'MXN' | 'ZAR' | 'KRW' | 'SGD' | 'HKD' | 'NOK' | 'SEK' | 'DKK' | 'CHF' | 'PLN' | 'CZK' | 'HUF' | 'RON' | 'BGN' | 'HRK' | 'RUB' | 'TRY' | 'ILS' | 'AED' | 'SAR' | 'QAR' | 'KWD' | 'BHD' | 'OMR' | 'JOD' | 'LBP' | 'EGP' | 'MAD' | 'TND' | 'DZD' | 'LYD' | 'SDG' | 'ETB' | 'KES' | 'UGX' | 'TZS' | 'RWF' | 'BIF' | 'DJF' | 'SOS' | 'ERN' | 'SLL' | 'GMD' | 'GNF' | 'LRD' | 'CDF' | 'AOA' | 'ZMW' | 'BWP' | 'SZL' | 'LSL' | 'NAD' | 'MZN' | 'MWK' | 'ZWL' | 'BOB' | 'CLP' | 'COP' | 'PEN' | 'UYU' | 'VES' | 'ARS' | 'PYG' | 'GTQ' | 'HNL' | 'NIO' | 'CRC' | 'PAB' | 'DOP' | 'HTG' | 'JMD' | 'TTD' | 'BBD' | 'XCD' | 'AWG' | 'BZD' | 'GYD' | 'SRD' | 'VND' | 'THB' | 'MYR' | 'IDR' | 'MMK' | 'LAK' | 'KHR' | 'BDT' | 'LKR' | 'NPR' | 'PKR' | 'AFN' | 'TJS' | 'UZS' | 'KZT' | 'KGS' | 'TMT' | 'AZN' | 'AMD' | 'GEL' | 'MDL' | 'UAH' | 'BYN' | 'MNT';

@Injectable({
  providedIn: 'root',
})
export class LocalizedPricingService {
  private readonly defaultCurrencyMap: Record<string, CurrencyInfo> = {
    // North America
    US: { symbol: '$', code: 'USD', locale: 'en-US', position: 'before' },
    CA: { symbol: 'C$', code: 'CAD', locale: 'en-CA', position: 'before' },
    MX: { symbol: '$', code: 'MXN', locale: 'es-MX', position: 'before' },

    // Europe
    DE: { symbol: '€', code: 'EUR', locale: 'de-DE', position: 'after' },
    FR: { symbol: '€', code: 'EUR', locale: 'fr-FR', position: 'after' },
    IT: { symbol: '€', code: 'EUR', locale: 'it-IT', position: 'after' },
    ES: { symbol: '€', code: 'EUR', locale: 'es-ES', position: 'after' },
    NL: { symbol: '€', code: 'EUR', locale: 'nl-NL', position: 'after' },
    BE: { symbol: '€', code: 'EUR', locale: 'nl-BE', position: 'after' },
    AT: { symbol: '€', code: 'EUR', locale: 'de-AT', position: 'after' },
    PT: { symbol: '€', code: 'EUR', locale: 'pt-PT', position: 'after' },
    IE: { symbol: '€', code: 'EUR', locale: 'en-IE', position: 'after' },
    FI: { symbol: '€', code: 'EUR', locale: 'fi-FI', position: 'after' },
    LU: { symbol: '€', code: 'EUR', locale: 'fr-LU', position: 'after' },
    SI: { symbol: '€', code: 'EUR', locale: 'sl-SI', position: 'after' },
    SK: { symbol: '€', code: 'EUR', locale: 'sk-SK', position: 'after' },
    EE: { symbol: '€', code: 'EUR', locale: 'et-EE', position: 'after' },
    LV: { symbol: '€', code: 'EUR', locale: 'lv-LV', position: 'after' },
    LT: { symbol: '€', code: 'EUR', locale: 'lt-LT', position: 'after' },
    CY: { symbol: '€', code: 'EUR', locale: 'el-CY', position: 'after' },
    MT: { symbol: '€', code: 'EUR', locale: 'mt-MT', position: 'after' },
    GR: { symbol: '€', code: 'EUR', locale: 'el-GR', position: 'after' },
    GB: { symbol: '£', code: 'GBP', locale: 'en-GB', position: 'before' },
    CH: { symbol: 'CHF', code: 'CHF', locale: 'de-CH', position: 'after' },
    NO: { symbol: 'kr', code: 'NOK', locale: 'nb-NO', position: 'after' },
    SE: { symbol: 'kr', code: 'SEK', locale: 'sv-SE', position: 'after' },
    DK: { symbol: 'kr', code: 'DKK', locale: 'da-DK', position: 'after' },
    PL: { symbol: 'zł', code: 'PLN', locale: 'pl-PL', position: 'after' },
    CZ: { symbol: 'Kč', code: 'CZK', locale: 'cs-CZ', position: 'after' },
    HU: { symbol: 'Ft', code: 'HUF', locale: 'hu-HU', position: 'after' },
    RO: { symbol: 'lei', code: 'RON', locale: 'ro-RO', position: 'after' },
    BG: { symbol: 'лв', code: 'BGN', locale: 'bg-BG', position: 'after' },
    HR: { symbol: 'kn', code: 'HRK', locale: 'hr-HR', position: 'after' },
    RU: { symbol: '₽', code: 'RUB', locale: 'ru-RU', position: 'after' },
    TR: { symbol: '₺', code: 'TRY', locale: 'tr-TR', position: 'after' },
    UA: { symbol: '₴', code: 'UAH', locale: 'uk-UA', position: 'after' },
    BY: { symbol: 'Br', code: 'BYN', locale: 'be-BY', position: 'before' },
    MD: { symbol: 'L', code: 'MDL', locale: 'ro-MD', position: 'before' },
    GE: { symbol: '₾', code: 'GEL', locale: 'ka-GE', position: 'before' },
    AM: { symbol: '֏', code: 'AMD', locale: 'hy-AM', position: 'after' },
    AZ: { symbol: '₼', code: 'AZN', locale: 'az-AZ', position: 'before' },
    KZ: { symbol: '₸', code: 'KZT', locale: 'kk-KZ', position: 'after' },
    KG: { symbol: 'лв', code: 'KGS', locale: 'ky-KG', position: 'before' },
    TM: { symbol: 'T', code: 'TMT', locale: 'tk-TM', position: 'before' },
    UZ: { symbol: 'лв', code: 'UZS', locale: 'uz-UZ', position: 'before' },
    TJ: { symbol: 'SM', code: 'TJS', locale: 'tg-TJ', position: 'before' },
    AF: { symbol: '؋', code: 'AFN', locale: 'fa-AF', position: 'before' },
    MN: { symbol: '₮', code: 'MNT', locale: 'mn-MN', position: 'before' },

    // Asia Pacific
    IN: { symbol: '₹', code: 'INR', locale: 'en-IN', position: 'before' },
    CN: { symbol: '¥', code: 'CNY', locale: 'zh-CN', position: 'before' },
    JP: { symbol: '¥', code: 'JPY', locale: 'ja-JP', position: 'before' },
    KR: { symbol: '₩', code: 'KRW', locale: 'ko-KR', position: 'before' },
    SG: { symbol: 'S$', code: 'SGD', locale: 'en-SG', position: 'before' },
    HK: { symbol: 'HK$', code: 'HKD', locale: 'en-HK', position: 'before' },
    AU: { symbol: 'A$', code: 'AUD', locale: 'en-AU', position: 'before' },
    NZ: { symbol: 'NZ$', code: 'NZD', locale: 'en-NZ', position: 'before' },
    TH: { symbol: '฿', code: 'THB', locale: 'th-TH', position: 'before' },
    MY: { symbol: 'RM', code: 'MYR', locale: 'ms-MY', position: 'before' },
    ID: { symbol: 'Rp', code: 'IDR', locale: 'id-ID', position: 'before' },
    PH: { symbol: '₱', code: 'PHP', locale: 'en-PH', position: 'before' },
    VN: { symbol: '₫', code: 'VND', locale: 'vi-VN', position: 'after' },
    MM: { symbol: 'K', code: 'MMK', locale: 'my-MM', position: 'before' },
    LA: { symbol: '₭', code: 'LAK', locale: 'lo-LA', position: 'before' },
    KH: { symbol: '៛', code: 'KHR', locale: 'km-KH', position: 'after' },
    BD: { symbol: '৳', code: 'BDT', locale: 'bn-BD', position: 'before' },
    LK: { symbol: '₨', code: 'LKR', locale: 'si-LK', position: 'before' },
    NP: { symbol: '₨', code: 'NPR', locale: 'ne-NP', position: 'before' },
    PK: { symbol: '₨', code: 'PKR', locale: 'ur-PK', position: 'before' },

    // Middle East
    IL: { symbol: '₪', code: 'ILS', locale: 'he-IL', position: 'before' },
    AE: { symbol: 'د.إ', code: 'AED', locale: 'ar-AE', position: 'before' },
    SA: { symbol: 'ر.س', code: 'SAR', locale: 'ar-SA', position: 'before' },
    QA: { symbol: 'ر.ق', code: 'QAR', locale: 'ar-QA', position: 'before' },
    KW: { symbol: 'د.ك', code: 'KWD', locale: 'ar-KW', position: 'before' },
    BH: { symbol: 'د.ب', code: 'BHD', locale: 'ar-BH', position: 'before' },
    OM: { symbol: 'ر.ع.', code: 'OMR', locale: 'ar-OM', position: 'before' },
    JO: { symbol: 'د.ا', code: 'JOD', locale: 'ar-JO', position: 'before' },
    LB: { symbol: 'ل.ل', code: 'LBP', locale: 'ar-LB', position: 'before' },

    // Africa
    EG: { symbol: '£', code: 'EGP', locale: 'ar-EG', position: 'before' },
    MA: { symbol: 'د.م.', code: 'MAD', locale: 'ar-MA', position: 'before' },
    TN: { symbol: 'د.ت', code: 'TND', locale: 'ar-TN', position: 'before' },
    DZ: { symbol: 'د.ج', code: 'DZD', locale: 'ar-DZ', position: 'before' },
    LY: { symbol: 'ل.د', code: 'LYD', locale: 'ar-LY', position: 'before' },
    SD: { symbol: 'ج.س.', code: 'SDG', locale: 'ar-SD', position: 'before' },
    ET: { symbol: 'Br', code: 'ETB', locale: 'am-ET', position: 'before' },
    KE: { symbol: 'KSh', code: 'KES', locale: 'sw-KE', position: 'before' },
    UG: { symbol: 'USh', code: 'UGX', locale: 'sw-UG', position: 'before' },
    TZ: { symbol: 'TSh', code: 'TZS', locale: 'sw-TZ', position: 'before' },
    RW: { symbol: 'RF', code: 'RWF', locale: 'rw-RW', position: 'before' },
    BI: { symbol: 'FBu', code: 'BIF', locale: 'rn-BI', position: 'before' },
    DJ: { symbol: 'Fdj', code: 'DJF', locale: 'fr-DJ', position: 'before' },
    SO: { symbol: 'S', code: 'SOS', locale: 'so-SO', position: 'before' },
    ER: { symbol: 'Nfk', code: 'ERN', locale: 'ti-ER', position: 'before' },
    SL: { symbol: 'Le', code: 'SLL', locale: 'en-SL', position: 'before' },
    GM: { symbol: 'D', code: 'GMD', locale: 'en-GM', position: 'before' },
    GN: { symbol: 'FG', code: 'GNF', locale: 'fr-GN', position: 'before' },
    LR: { symbol: 'L$', code: 'LRD', locale: 'en-LR', position: 'before' },
    CD: { symbol: 'FC', code: 'CDF', locale: 'fr-CD', position: 'before' },
    AO: { symbol: 'Kz', code: 'AOA', locale: 'pt-AO', position: 'before' },
    ZM: { symbol: 'ZK', code: 'ZMW', locale: 'en-ZM', position: 'before' },
    BW: { symbol: 'P', code: 'BWP', locale: 'en-BW', position: 'before' },
    SZ: { symbol: 'L', code: 'SZL', locale: 'en-SZ', position: 'before' },
    LS: { symbol: 'L', code: 'LSL', locale: 'en-LS', position: 'before' },
    NA: { symbol: 'N$', code: 'NAD', locale: 'en-NA', position: 'before' },
    MZ: { symbol: 'MT', code: 'MZN', locale: 'pt-MZ', position: 'before' },
    MW: { symbol: 'MK', code: 'MWK', locale: 'en-MW', position: 'before' },
    ZW: { symbol: 'Z$', code: 'ZWL', locale: 'en-ZW', position: 'before' },
    ZA: { symbol: 'R', code: 'ZAR', locale: 'en-ZA', position: 'before' },

    // South America
    BR: { symbol: 'R$', code: 'BRL', locale: 'pt-BR', position: 'before' },
    AR: { symbol: '$', code: 'ARS', locale: 'es-AR', position: 'before' },
    CL: { symbol: '$', code: 'CLP', locale: 'es-CL', position: 'before' },
    CO: { symbol: '$', code: 'COP', locale: 'es-CO', position: 'before' },
    PE: { symbol: 'S/', code: 'PEN', locale: 'es-PE', position: 'before' },
    UY: { symbol: '$U', code: 'UYU', locale: 'es-UY', position: 'before' },
    VE: { symbol: 'Bs.S', code: 'VES', locale: 'es-VE', position: 'before' },
    PY: { symbol: '₲', code: 'PYG', locale: 'es-PY', position: 'before' },
    BO: { symbol: 'Bs', code: 'BOB', locale: 'es-BO', position: 'before' },

    // Central America & Caribbean
    GT: { symbol: 'Q', code: 'GTQ', locale: 'es-GT', position: 'before' },
    HN: { symbol: 'L', code: 'HNL', locale: 'es-HN', position: 'before' },
    NI: { symbol: 'C$', code: 'NIO', locale: 'es-NI', position: 'before' },
    CR: { symbol: '₡', code: 'CRC', locale: 'es-CR', position: 'before' },
    PA: { symbol: 'B/.', code: 'PAB', locale: 'es-PA', position: 'before' },
    DO: { symbol: 'RD$', code: 'DOP', locale: 'es-DO', position: 'before' },
    HT: { symbol: 'G', code: 'HTG', locale: 'fr-HT', position: 'before' },
    JM: { symbol: 'J$', code: 'JMD', locale: 'en-JM', position: 'before' },
    TT: { symbol: 'TT$', code: 'TTD', locale: 'en-TT', position: 'before' },
    BB: { symbol: 'Bds$', code: 'BBD', locale: 'en-BB', position: 'before' },
    AG: { symbol: '$', code: 'XCD', locale: 'en-AG', position: 'before' },
    AW: { symbol: 'ƒ', code: 'AWG', locale: 'nl-AW', position: 'before' },
    BZ: { symbol: 'BZ$', code: 'BZD', locale: 'en-BZ', position: 'before' },
    GY: { symbol: 'G$', code: 'GYD', locale: 'en-GY', position: 'before' },
    SR: { symbol: '$', code: 'SRD', locale: 'nl-SR', position: 'before' },
  };

  constructor(
    private readonly growthBookService: GrowthBookService,
    private readonly geoLocationService: GeoLocationService,
  ) { }

  async getCurrencyInfo(): Promise<CurrencyInfo> {
    try {
      const userGeoLocation = await this.geoLocationService.getGeoInfo();
      const countryCode = userGeoLocation?.geo?.country;

      // Check if country is in our supported map
      if (countryCode && this.defaultCurrencyMap[countryCode]) {
        return this.defaultCurrencyMap[countryCode];
      }
    } catch (error) {
      // Failed to get geo location for currency detection
    }

    // Fallback to USD for unsupported countries or errors
    return this.defaultCurrencyMap['US'];
  }

  async formatPrice(price: number, context?: 'excludePlanAndAddon'): Promise<string> {
    const currencyInfo = await this.getCurrencyInfo();

    // Determine decimal places based on currency and context
    const decimalPlaces = this.getDecimalPlacesForCurrency(currencyInfo.code, context);

    // Use proper locale-based formatting
    const formattedNumber = new Intl.NumberFormat(currencyInfo.locale, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(price);

    return currencyInfo.symbol + formattedNumber;
  }

  isLocalizedPricingEnabled(): boolean {
    const featureValue = this.growthBookService.getFeatureValue('localized-pricing', false);
    // Return true only if feature value is explicitly true
    return featureValue === true;
  }

  /**
   * Determines the correct currency and price based on billing currency and priceOverrides
   * @param priceOverrides - The price override data from the plan
   * @param defaultPrice - The default price if no override is found
   * @param billingCurrency - The currency from billing details (if available)
   * @returns Promise<{currency: string, price: number}>
   */
  async getLocalizedPrice(
    priceOverrides?: UnitPriceOverride[],
    defaultPrice?: number,
    billingCurrency?: string,
  ): Promise<{currency: string, price: number}> {
    try {
      // Priority 1: Use billing currency if available and there's a matching price override
      if (billingCurrency && priceOverrides && priceOverrides.length > 0) {
        const matchingOverride = priceOverrides.find(override => override.currencyCode === billingCurrency);
        if (matchingOverride) {
          return {
            currency: matchingOverride.currencyCode,
            price: convertCentsToDollars(+matchingOverride.unitPrice),
          };
        }
      }
      // Priority 2: Use geo-based pricing if localized pricing is enabled and no billing currency
      const isEnabled = this.isLocalizedPricingEnabled();
      if (isEnabled && !billingCurrency) {
        const geoBasedPrice = await this.getGeoBasedPrice(priceOverrides, defaultPrice);
        if (geoBasedPrice) {
          return geoBasedPrice;
        }
      }

      // Priority 3: Fallback to default currency based on user location
      const currencyInfo = await this.getCurrencyInfo();
      return {
        currency: currencyInfo.code,
        price: defaultPrice || 0,
      };
    } catch (error) {
      // Error getting geo location in getLocalizedPrice, falling back to default currency
      // Fallback to USD when geo location fails
      return {
        currency: 'USD',
        price: defaultPrice || 0,
      };
    }
  }


  /**
   * Gets geo-based pricing for a user's location
   * @param priceOverrides - The price override data from the plan
   * @param defaultPrice - The default price if no override is found
   * @returns Promise<{currency: string, price: number} | null>
   */
  private async getGeoBasedPrice(
    priceOverrides?: UnitPriceOverride[],
    defaultPrice?: number,
  ): Promise<{currency: string, price: number} | null> {
    try {
      // If no price overrides available, no geo-based pricing possible
      if (!priceOverrides || priceOverrides.length === 0) {
        return null;
      }

      const userGeoLocation = await this.geoLocationService.getGeoInfo();
      const countryCode = userGeoLocation.geo?.country;

      if (!countryCode) {
        return null;
      }

      // Check if we have price overrides for this country
      const matchingOverride = priceOverrides.find(override =>
        override.countryCode?.toUpperCase() === countryCode.toUpperCase(),
      );

      if (matchingOverride) {
        return {
          currency: matchingOverride.currencyCode,
          price: convertCentsToDollars(+matchingOverride.unitPrice),
        };
      }

      // If no price override found for user's country, return null
      // This ensures geo-based pricing is only used when there's a relevant price override
      return null;
    } catch (error) {
      // Error getting geo-based price
      return null;
    }
  }

  /**
   * Gets the currency symbol for display purposes
   * @param currencyCode - The currency code (USD, PHP, INR, etc.)
   * @returns string - The currency symbol
   */
  getCurrencySymbol(currencyCode: string): string {
    if (!currencyCode) {
      return '$';
    }
    const currencyInfo = Object.values(this.defaultCurrencyMap).find(info => info.code === currencyCode);
    return currencyInfo?.symbol || '$';
  }

  /**
   * Determines the number of decimal places to display for a given currency
   * @param currencyCode - The currency code
   * @param context - Optional context to determine decimal places (excludePlanAndAddon for special cases)
   * @returns number - Number of decimal places (0 for INR in plan/addon contexts, 2 for most others)
   */
  private getDecimalPlacesForCurrency(currencyCode: string, context?: 'excludePlanAndAddon'): number {
    // INR should display without decimal places only in plan selection and addon pages
    if (currencyCode.toUpperCase() === 'INR' && !context) {
      return 0;
    }

    // All other currencies use 2 decimal places, including INR in non-plan/addon contexts
    return 2;
  }

  /**
   * Formats a price with the appropriate currency symbol and locale-based number formatting
   * @param price - The price to format
   * @param currencyCode - The currency code
   * @param context - Optional context to determine decimal places (excludePlanAndAddon for special cases)
   * @returns string - Formatted price with currency symbol and proper number formatting
   */
  formatPriceWithCurrency(price: number, currencyCode: string, context?: 'excludePlanAndAddon'): string {
    const symbol = this.getCurrencySymbol(currencyCode);
    const numericPrice = typeof price === 'number' && !Number.isNaN(price) ? price : 0;

    // Get locale information for proper number formatting
    const currencyInfo = this.getCurrencyInfoByCode(currencyCode);
    const locale = currencyInfo.locale;

    // Determine decimal places based on currency and context
    const decimalPlaces = this.getDecimalPlacesForCurrency(currencyCode, context);

    // Format the number with proper locale-based formatting (includes commas for thousands)
    const formattedNumber = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(Math.abs(numericPrice));

    // Handle negative values properly
    if (numericPrice < 0) {
      return `- ${symbol}${formattedNumber}`;
    }

    return `${symbol}${formattedNumber}`;
  }

  /**
   * Extracts currency code from plan's price overrides
   * @param plan - The plan containing price overrides
   * @returns string - Currency code or 'USD' as fallback
   */
  getCurrencyFromPlan(plan: any): string {
    const currency = plan?.billingApiData?.billingDetails?.currency;
    if (currency) {
      return currency;
    }

    // Check if localized pricing is enabled
    if (!this.isLocalizedPricingEnabled()) {
      return 'USD';
    }

    // If no card is added yet, try to get currency from price overrides based on user's country
    if (!plan?.paymentModel?.cardLast4 && plan?.priceOverrides?.length) {
      try {
        // Get cached geo info synchronously
        const cachedGeoInfo = this.geoLocationService.getCachedGeoInfo();
        const userCountryCode = cachedGeoInfo?.geo?.country;

        if (userCountryCode) {
          // Find price override that matches user's country code
          const matchingOverride = plan.priceOverrides.find(
            override => override.countryCode?.toUpperCase() === userCountryCode.toUpperCase(),
          );

          if (matchingOverride?.currencyCode) {
            return matchingOverride.currencyCode;
          }
        }
      } catch (error) {
        // Error getting currency from price overrides
      }
    }

    return 'USD';
  }

  /**
   * Gets currency information based on billing details currency or user's country
   * @param billingDetails - Optional billing details containing currency information
   * @returns Promise<CurrencyInfo> - Currency information
   */
  async getCurrencyInfoFromBilling(billingDetails?: any): Promise<CurrencyInfo> {
    // First priority: Use currency from billing details if available
    if (billingDetails?.currency) {
      return this.getCurrencyInfoByCode(billingDetails.currency);
    }

    // Second priority: Try to get currency from user's country via geo info
    try {
      const geoInfo = await this.geoLocationService.getGeoInfo();
      if (geoInfo?.geo?.country) {
        const countryCode = geoInfo.geo.country;
        if (this.defaultCurrencyMap[countryCode]) {
          return this.defaultCurrencyMap[countryCode];
        }
      }
    } catch (error) {
      // Failed to get geo info for currency detection
    }

    // Default fallback: USD
    return this.getCurrencyInfoByCode('USD');
  }

  /**
   * Gets currency information by currency code with validation
   * @param currencyCode - The currency code
   * @returns CurrencyInfo - Currency information
   */
  getCurrencyInfoByCode(currencyCode: string): CurrencyInfo {
    const normalizedCode = currencyCode.toUpperCase();

    // Find currency info from our map
    const currencyInfo = Object.values(this.defaultCurrencyMap).find(info => info.code === normalizedCode);

    if (currencyInfo) {
      return currencyInfo;
    }

    // Invalid currency code, falling back to USD
    return this.defaultCurrencyMap['US'];
  }

  /**
   * Gets currency information by country code
   * @param countryCode - The country code (e.g., 'US', 'IN', 'DE')
   * @returns CurrencyInfo - Currency information for the country
   */
  getCurrencyInfoByCountry(countryCode: string): CurrencyInfo {
    const normalizedCountryCode = countryCode.toUpperCase();

    if (this.defaultCurrencyMap[normalizedCountryCode]) {
      return this.defaultCurrencyMap[normalizedCountryCode];
    }

    // Country not supported, falling back to USD
    return this.defaultCurrencyMap['US'];
  }

  /**
   * Gets cached geo information synchronously
   * @returns GeoInfo | null - Cached geo information or null if not available
   */
  getCachedGeoInfo(): any {
    return this.geoLocationService.getCachedGeoInfo();
  }

  /**
   * Validates if a currency code is supported
   * @param code - The currency code to validate
   * @returns boolean - Whether the currency code is supported
   */
  isValidCurrencyCode(code: string): boolean {
    return Object.values(this.defaultCurrencyMap).some(info => info.code === code.toUpperCase());
  }

  /**
   * Gets the localized unit price for a plan during initialization
   * @param plan - The plan API data
   * @param billingDetails - The billing details containing currency information
   * @returns number - The localized unit price in dollars
   */
  getLocalizedUnitPrice(plan: any, billingDetails?: any): number {
    // Priority 1: Use currency from billing details if available
    if (billingDetails?.currency && plan?.priceOverrides) {
      const override = plan.priceOverrides.find(
        (po: any) => po.currencyCode === billingDetails.currency,
      );
      if (override) {
        return convertCentsToDollars(+override.unitPrice);
      }
    }

    // Priority 2: Try to get user's country from geo info and use price override
    if (plan?.priceOverrides) {
      // For now, we'll use the default price
      // You could enhance this by getting the actual country from geo info
    }

    // Priority 3: Default to original unit price
    return convertCentsToDollars(plan?.unitPrice || 0);
  }

  /**
   * Formats USD price with proper negative value handling
   * @param price - The price to format
   * @returns string - Formatted USD price
   */
  private formatUSDPrice(price: number): string {
    const numericPrice = normalizePrice(price);

    // Use proper locale-based formatting for USD
    const formattedNumber = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Math.abs(numericPrice));

    if (numericPrice < 0) {
      return `-$${formattedNumber}`;
    }

    return `$${formattedNumber}`;
  }

  /**
   * Gets localized price and currency for a plan when no card is added
   * @param plan - The plan containing price overrides
   * @param defaultPrice - The default price if no override is found
   * @returns Object with currency and price, or null if not found
   */
  getLocalizedPriceForNoCard(plan: ConcretePlan , defaultPrice: number): {currency: string, price: number} | null {
    // Only proceed if no card is added and price overrides exist
    if (plan?.paymentModel?.cardLast4 || !plan?.priceOverrides?.length) {
      return null;
    }

    // Check if localized pricing is enabled
    if (!this.isLocalizedPricingEnabled()) {
      return null;
    }

    try {
      // Get cached geo info synchronously
      const cachedGeoInfo = this.geoLocationService.getCachedGeoInfo();
      const userCountryCode = cachedGeoInfo?.geo?.country;

      if (userCountryCode) {
        // Find price override that matches user's country code
        const matchingOverride = plan.priceOverrides.find(
          override => override.countryCode?.toUpperCase() === userCountryCode.toUpperCase(),
        );

        if (matchingOverride?.currencyCode && matchingOverride?.unitPrice) {
          return {
            currency: matchingOverride.currencyCode,
            price: (+matchingOverride.unitPrice) / 100,
          };
        }
      }
    } catch (error) {
      // Error getting localized price for no card scenario
    }

    return null;
  }

  /**
   * Centralized method to format localized price for components
   * @param price - The price to format
   * @param plan - The plan containing price overrides
   * @param fallbackToUSD - Whether to fallback to USD formatting if localized pricing is disabled
   * @param context - Optional context to determine decimal places (excludePlanAndAddon for special cases)
   * @returns string - Formatted price with appropriate currency
   */
  formatLocalizedPriceForComponent(price: number, plan?: any, fallbackToUSD: boolean = true, context?: 'excludePlanAndAddon'): string {
    const billingCurrency = plan?.billingApiData?.billingDetails?.currency;

    // Priority 1: Use billing currency only if available and has matching price override
    if (billingCurrency && plan) {
      // Check if plan has a matching priceOverride for billing currency
      const hasMatchingOverride = plan?.priceOverrides?.some(
        override => override.currencyCode === billingCurrency,
      );

      if (hasMatchingOverride) {
        const numericPrice = normalizePrice(price);
        return this.formatPriceWithCurrency(numericPrice, billingCurrency, context);
      }
    }

    // Priority 2: Use geo-based currency only if localized pricing is enabled and no billing currency
    const isEnabled = this.isLocalizedPricingEnabled();
    if (isEnabled && !billingCurrency && plan) {
      // Try to get geo-based pricing asynchronously, but for synchronous calls, use cached currency
      const currency = this.getCurrencyFromPlan(plan);
      const numericPrice = normalizePrice(price);

      if (currency !== 'USD') {
        return this.formatPriceWithCurrency(numericPrice, currency, context);
      }
    }

    // Priority 3: Fallback to USD
    if (fallbackToUSD) {
      return this.formatUSDPrice(price);
    }

    return normalizePrice(price).toString();
  }

  /**
   * Async version of formatLocalizedPriceForComponent that can use geo-based pricing
   * @param price - The price to format
   * @param plan - The plan containing price overrides
   * @param fallbackToUSD - Whether to fallback to USD formatting if localized pricing is disabled
   * @param context - Optional context to determine decimal places (excludePlanAndAddon for special cases)
   * @returns Promise<string> - Formatted price with appropriate currency
   */
  async formatLocalizedPriceForComponentAsync(price: number, plan?: any, fallbackToUSD: boolean = true, context?: 'excludePlanAndAddon'): Promise<string> {
    const billingCurrency = plan?.billingApiData?.billingDetails?.currency;

    // Priority 1: Use billing currency only if available and has matching price override
    if (billingCurrency && plan) {
      // Check if plan has a matching priceOverride for billing currency
      const hasMatchingOverride = plan?.priceOverrides?.some(
        override => override.currencyCode === billingCurrency,
      );

      if (hasMatchingOverride) {
        const numericPrice = normalizePrice(price);
        return this.formatPriceWithCurrency(numericPrice, billingCurrency, context);
      }
    }

    // Priority 2: Use geo-based pricing if localized pricing is enabled and no billing currency
    const isEnabled = this.isLocalizedPricingEnabled();
    if (isEnabled && !billingCurrency && plan) {
      try {
        const geoBasedPrice = await this.getGeoBasedPrice(plan?.priceOverrides, price);
        if (geoBasedPrice) {
          return this.formatPriceWithCurrency(geoBasedPrice.price, geoBasedPrice.currency, context);
        }
      } catch (error) {
        // Error getting geo-based price for component
      }
    }

    // Priority 3: Fallback to USD
    if (fallbackToUSD) {
      return this.formatUSDPrice(price);
    }

    return normalizePrice(price).toString();
  }

}
